import { Stack } from '@mui/material'
import { ColumnDef } from '@tanstack/react-table'
import { tokens } from 'ComponentLibrary'
import type { MonetaryValueDataObjectType, MoneyPerAreaValue } from 'ComponentLibrary/src/common/money'
import { isMonetaryValueDataObject } from 'ComponentLibrary/src/common/money'
import { SimpleTableCell } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableCell'
import { SimpleTableHeadline } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableHeadline'
import {
    SimpleTableMonetaryCell,
    SimpleTableNumberCell,
    SimpleTablePercentageCell,
    SimpleTableTextCell,
} from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableTextCells'
import { SimpleTable } from 'ComponentLibrary/src/piazza/tables/SimpleTable/SimpleTable'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'

type MarketDataLandLabels = {
    category?: string
    type?: string
    base_value?: string
    current_value?: string
}

type MarketDataLandValueType = 'number' | 'percentage' | 'monetary_value' | 'money_per_area_value'

type MarketDataLandCurrentValue<TValue> = {
    minimum?: TValue
    average?: TValue
    maximum?: TValue
}

type MarketDataLandEntryBase = {
    category?: string
    type?: string
}

type MarketDataLandValueMap = {
    number: number
    percentage: number
    monetary_value: MonetaryValueDataObjectType
    money_per_area_value: MoneyPerAreaValue
}

type MarketDataLandValue = MarketDataLandValueMap[MarketDataLandValueType]

type MarketDataLandEntry<TType extends MarketDataLandValueType> = MarketDataLandEntryBase & {
    value_type?: TType
    base_value?: string
    current_value?: MarketDataLandCurrentValue<MarketDataLandValueMap[TType]>
}

export type ValueCheckLandMarketData = {
    labels?: MarketDataLandLabels
    entries: MarketDataLandEntry<MarketDataLandValueType>[]
}

type ValueCheckMarketDataLandWidgetProps = {
    landMarketData: ValueCheckLandMarketData
    copy: ValueCheckMarketDataLandWidgetCopy
    locale?: string
}

export type ValueCheckMarketDataLandWidgetCopy = {
    title: string
    rowLabels: {
        minimum: string
        average: string
        maximum: string
    }
    columnLabels: {
        previousValuation: string
    }
}

type LabelRow = {
    id: 'labels'
    rowType: 'labels'
    category?: string
    type?: string
    baseValue?: string
    currentValue?: string
}

type SecondLabelRow = {
    id: 'minimum-maximum-average-labels'
    rowType: 'minimum-maximum-average-labels'
}

type EntryRow = {
    id: string
    rowType: 'entry'
    valueType: MarketDataLandValueType
    category?: string
    type?: string
    baseValue?: string
    minimum?: MarketDataLandValue
    maximum?: MarketDataLandValue
    average?: MarketDataLandValue
}

type LandMarketReportRow = LabelRow | SecondLabelRow | EntryRow

const renderLandValueCell = (valueType: MarketDataLandValueType, value?: MarketDataLandValue, locale = 'de-DE') => {
    if (value === undefined) {
        return <SimpleTableCell alignment="right" />
    }

    switch (valueType) {
        case 'number':
            return (
                <SimpleTableNumberCell
                    number={typeof value === 'number' ? value : undefined}
                    numberOfDigits={2}
                    alignment="right"
                    locale={locale}
                />
            )
        case 'percentage':
            return (
                <SimpleTablePercentageCell
                    percentage={typeof value === 'number' ? value : undefined}
                    numberOfDigits={2}
                    alignment="right"
                    locale={locale}
                />
            )
        case 'monetary_value':
            return (
                <SimpleTableMonetaryCell
                    monetaryValue={isMonetaryValueDataObject(value) ? value : undefined}
                    alignment="right"
                    locale={locale}
                />
            )
        case 'money_per_area_value': {
            return (
                <SimpleTableMonetaryCell
                    monetaryValue={isMonetaryValueDataObject(value) ? value : undefined}
                    additionalUnit="/m²"
                    alignment="right"
                    locale={locale}
                />
            )
        }
    }
}

export default function ValueCheckMarketDataLandWidget({ landMarketData, copy, locale = 'de-DE' }: ValueCheckMarketDataLandWidgetProps) {
    const rows: LandMarketReportRow[] = [
        {
            id: 'labels',
            rowType: 'labels',
            category: landMarketData.labels?.category,
            type: landMarketData.labels?.type,
            baseValue: landMarketData.labels?.base_value,
            currentValue: landMarketData.labels?.current_value,
        },
        {
            id: 'minimum-maximum-average-labels',
            rowType: 'minimum-maximum-average-labels',
        },
        ...(landMarketData.entries || []).map((entry, index) => ({
            id: `land-entry-${index}`,
            rowType: 'entry' as const,
            valueType: entry.value_type ?? 'number',
            category: entry.category,
            type: entry.type,
            baseValue: entry.base_value,
            minimum: entry.current_value?.minimum,
            maximum: entry.current_value?.maximum,
            average: entry.current_value?.average,
        })),
    ]

    const columns: ColumnDef<LandMarketReportRow>[] = [
        {
            id: 'category',
            cell: ({ row }) => {
                if (row.original.rowType === 'labels') {
                    return row.original.category ? (
                        <SimpleTableHeadline text={row.original.category} variant="primary" />
                    ) : (
                        <SimpleTableCell />
                    )
                }
                if (row.original.rowType === 'entry') {
                    return row.original.category ? <SimpleTableTextCell text={row.original.category} /> : <SimpleTableCell />
                }
                return <SimpleTableCell />
            },
        },
        {
            id: 'type',
            cell: ({ row }) => {
                if (row.original.rowType === 'labels') {
                    return (
                        <Stack width="100%" borderLeft={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}>
                            {row.original.type ? <SimpleTableHeadline text={row.original.type} variant="primary" /> : <SimpleTableCell />}
                        </Stack>
                    )
                }
                if (row.original.rowType === 'entry') {
                    return (
                        <Stack width="100%" borderLeft={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}>
                            {row.original.type ? <SimpleTableTextCell text={row.original.type} /> : <SimpleTableCell />}
                        </Stack>
                    )
                }
                return (
                    <Stack width="100%" borderLeft={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}>
                        <SimpleTableCell />
                    </Stack>
                )
            },
        },
        {
            id: 'base-value',
            cell: ({ row }) => {
                if (row.original.rowType === 'labels') {
                    return (
                        <Stack width="100%" borderLeft={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}>
                            {row.original.baseValue ? (
                                <SimpleTableHeadline text={row.original.baseValue} variant="primary" alignment="right" />
                            ) : (
                                <SimpleTableCell alignment="right" />
                            )}
                        </Stack>
                    )
                }
                if (row.original.rowType === 'entry') {
                    return (
                        <Stack width="100%" borderLeft={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}>
                            {row.original.baseValue ? (
                                <SimpleTableTextCell text={row.original.baseValue} alignment="right" />
                            ) : (
                                <SimpleTableCell alignment="right" />
                            )}
                        </Stack>
                    )
                }
                return (
                    <Stack width="100%" borderLeft={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}>
                        <SimpleTableCell alignment="right" />
                    </Stack>
                )
            },
        },
        {
            id: 'minimum',
            cell: ({ row }) => {
                if (row.original.rowType === 'labels') {
                    return (
                        <Stack width="100%" borderLeft={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}>
                            <SimpleTableCell alignment="right" />
                        </Stack>
                    )
                }
                if (row.original.rowType === 'minimum-maximum-average-labels') {
                    return (
                        <Stack width="100%" borderLeft={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}>
                            <SimpleTableHeadline text={copy.rowLabels.minimum} variant="secondary" alignment="right" />
                        </Stack>
                    )
                }
                return (
                    <Stack width="100%" borderLeft={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}>
                        {renderLandValueCell(row.original.valueType, row.original.minimum, locale)}
                    </Stack>
                )
            },
        },
        {
            id: 'maximum',
            cell: ({ row }) => {
                if (row.original.rowType === 'minimum-maximum-average-labels') {
                    return <SimpleTableHeadline text={copy.rowLabels.maximum} variant="secondary" alignment="right" />
                }
                if (row.original.rowType === 'entry') {
                    return renderLandValueCell(row.original.valueType, row.original.maximum, locale)
                }
                return <SimpleTableCell alignment="right" />
            },
        },
        {
            id: 'average',
            cell: ({ row }) => {
                if (row.original.rowType === 'labels') {
                    return <SimpleTableHeadline text={copy.columnLabels.previousValuation} variant="primary" alignment="right" />
                }
                if (row.original.rowType === 'minimum-maximum-average-labels') {
                    return <SimpleTableHeadline text={copy.rowLabels.average} variant="secondary" alignment="right" />
                }
                if (row.original.rowType === 'entry') {
                    return renderLandValueCell(row.original.valueType, row.original.average, locale)
                }
                return <SimpleTableCell alignment="right" />
            },
        },
    ]

    return (
        <Stack direction="column" gap={tokens.SizingSpacing200}>
            <Headline title={copy.title} variant="headline3" />
            <SimpleTable data={rows} columns={columns} width="100%" backgroundColor={tokens.ColorSurfaceDefault} disableVirtualization />
        </Stack>
    )
}
